// --- Core ---
import {PropsWithChildren, useContext, useMemo} from "react";
import {find} from "underscore";
// --- SaniQ ---
import {TenantModule as TenantModuleType, TenantModuleConfig} from "../models/tenant/Configuration";
import {TenantModuleContext} from "src/config/tenantModules";
import {ModuleKey} from "vendor/qurasoft/definitions/types/shared/moduleKey";

type FindTenantModule = (module: TenantModuleType, keys: ModuleKey | ModuleKey[], exact?: boolean) => TenantModuleType;

const findTenantModule: FindTenantModule = (module, keys, exact = false) => {
  const keysArray = Array.isArray(keys) ? keys : [keys];

  if (keys && keysArray.length) {
    const nextKey = keysArray.shift();
    const nextModule = find(module.modules, m => m.key === nextKey);

    if (nextModule) {
      return findTenantModule(nextModule, keysArray, exact);
    } else {
      return exact ? null : module;
    }
  } else {
    return module;
  }
};

type UseTenantModule = (
  // Single element or array of keys to match the module
  keys: ModuleKey | ModuleKey[],
  // Whether the module must be an exact match, or can be a matching parent element
  exact?: boolean
) => TenantModuleType;

/**
 * Receive the memoized tenant configuration module specified by keys.
 */
const useTenantModule: UseTenantModule = (keys, exact = false) => {
  const tenantModules = useContext(TenantModuleContext);

  return useMemo(() => findTenantModule(tenantModules, keys, exact), [exact, keys, tenantModules]);
};

interface TenantModuleProps extends PropsWithChildren<Omit<TenantModuleType, "id" | "type" >> {
  /**
   * Filter based on the tenant module config. When present, display the children only if the the filter returns true.
   *
   * @param config Configuration object of the tenant module
   * @return {bool} Whether to display the children
   */
  configFilter?: (config: TenantModuleConfig) => boolean;
  /**
   * Whether the keys must be matched exactly or not
   *
   * Especially useful together with a configFilter.
   */
  exact?: boolean;
  /**
   * Array of keys describing the requested module. Starting from the top level the keys are searched for in order.
   * If a key cannot be found the parent module is selected and its enabled status is used.
   *
   * keys may also be a single string that is then wrapped in an array, containing only the single element.
   *
   * Example:
   * keys={["measurements", "triggers"]} to select the submodule triggers of measurements
   * keys={"measurements"} to select the measurements module
   */
  keys: ModuleKey | ModuleKey[];
}

/**
 * Show the children when the specified module is enabled for the tenant. Hides the children if it is disabled
 *
 * Internaly uses `useTenantModule` hook, to determine the specified module
 */
const TenantModule = ({children, configFilter, exact = false, keys}: TenantModuleProps) => {
  const module = useTenantModule(keys, exact);
  const enabledThroughConfig = useMemo(() => (module && configFilter) ? configFilter(module.config || {}) : true, [configFilter, module]);

  if (module?.isEnabled && enabledThroughConfig) {
    return <>{children}</>;
  }

  return null;
};

export {
  findTenantModule,
  TenantModule,
  useTenantModule
};
